import { useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import {
  containerStyle,
  likeContainerStyle
} from '@/components/Likes/styles.ts';
import { useLikesSentListStore } from '@/store/useLikesSentListStore.ts';
import LikesListSkeleton from '@/components/Likes/LikesListSkeleton/LikesListSkeleton.tsx';
import SentLikeCard from '@/components/Likes/LikeCard/SentLikeCard.tsx';
import PremiumUpsaleLikeCard from '@/components/Likes/LikeCard/PremiumUpsaleLikeCard.tsx';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import Modal from '@/animations/Modal.tsx';
import { CandidateProfileApi } from '@/api/matchmaker/types.ts';
import SendGiftPopup from '@/components/Discovery/SendGiftPopup/SendGiftPopup.tsx';
import UserProfilePopup from '@/components/Popup/UserProfile/UserProfilePopup.tsx';
import { PaywallTypes, ProductInvoicePlacements } from '@/enums/apiEnums.ts';
import SentLikesListBanner from '@/components/Likes/HeaderBanners/SentLikesListBanner.tsx';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';

const ROW_HEIGHT = 220;
const ROW_GAP = 15;
const COLUMNS = 2;

const SentLikesList = () => {
  const [payWallOpen, setPayWallOpen] = useState<boolean>(false);

  const [giftedCandidate, setGiftedCandidate] =
    useState<CandidateProfileApi | null>(null);

  const [userProfileOpened, setUserProfileOpened] = useState<boolean>(false);
  const [userProfileHid, setUserProfileHid] = useState<string | undefined>(
    undefined
  );

  const closeUserProfile = () => {
    setUserProfileOpened(false);
    setUserProfileHid(undefined);
  };
  const openUserProfile = (userHid: string) => {
    setUserProfileHid(userHid);
    setUserProfileOpened(true);
  };

  const { sentLikes, loadSentLikes, isFetching, reset } =
    useLikesSentListStore();

  const { remainingAmount } = useProfileStore();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // +1 for PremiumUpsaleLikeCard
  const totalCells = sentLikes.length + 1;
  const rowCount = Math.ceil(totalCells / COLUMNS);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT + ROW_GAP,
    overscan: 3
  });

  useEffect(() => {
    void loadSentLikes();

    return () => {
      reset();
    };
  }, []);

  const openSendGiftPopup = (candidate: CandidateProfileApi) => {
    setGiftedCandidate(candidate);
  };

  const closeSendGift = () => {
    setGiftedCandidate(null);
  };

  const sendReaction = (candidate: CandidateProfileApi) => {
    try {
      openSendGiftPopup(candidate);
    } catch (e) {
      catchErrors(e, true);
    }
  };

  const isShowGiftPopup = giftedCandidate !== null;
  const virtualRows = virtualizer.getVirtualItems();

  const showSentLikesListBanner = remainingAmount < 1;

  const handleBannerClick = () => {
    setPayWallOpen(true);
  };

  return (
    <>
      <div
        ref={scrollContainerRef}
        style={{
          ...containerStyle(isFetching),
          overflow: isFetching ? 'hidden' : 'auto',
          flexWrap: undefined,
          display: 'block'
        }}
      >
        {showSentLikesListBanner && (
          <SentLikesListBanner onClick={handleBannerClick} />
        )}

        {isFetching && <LikesListSkeleton />}

        {!isFetching && (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: '100%',
              position: 'relative'
            }}
          >
            {virtualRows.map((virtualRow) => {
              const cellStart = virtualRow.index * COLUMNS;

              return (
                <div
                  key={virtualRow.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: ROW_HEIGHT,
                    transform: `translateY(${virtualRow.start}px)`,
                    display: 'flex',
                    gap: `${ROW_GAP}px`
                  }}
                >
                  {Array.from({ length: COLUMNS }).map((_, colIndex) => {
                    const cellIndex = cellStart + colIndex;

                    if (cellIndex >= totalCells) return null;

                    if (cellIndex === 0) {
                      return <PremiumUpsaleLikeCard key="premium-upsale" />;
                    }

                    const likeIndex = cellIndex - 1;
                    const item = sentLikes[likeIndex];
                    if (!item) return null;

                    return (
                      <div key={item.profile.hid} style={likeContainerStyle}>
                        <SentLikeCard
                          imgUrl={item.profile.v2photos[0]}
                          name={item.profile.name}
                          age={item.profile.age}
                          onClick={() => openUserProfile(item.profile.hid)}
                          onSendReaction={() => sendReaction(item.profile)}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {giftedCandidate && (
        <Modal fullScreen open={isShowGiftPopup} handleClose={closeSendGift}>
          <SendGiftPopup
            giftedContactId={giftedCandidate.hid}
            giftedContactName={giftedCandidate.name}
            giftedContactPhoto={giftedCandidate.v2photos[0]}
            handleClose={closeSendGift}
            remainingAmount={remainingAmount}
            placement={ProductInvoicePlacements.YOUR_LIKES_SEND_REACTION_BUTTON}
            hideBackButton={true}
            prevHeaderTemplate="light"
          />
        </Modal>
      )}

      <Modal fullScreen open={userProfileOpened} handleClose={closeUserProfile}>
        <UserProfilePopup
          handleClose={closeUserProfile}
          userHid={userProfileHid}
          usePrevHeaderColors={false}
          prevHeaderColors="light"
        />
      </Modal>

      <Modal
        fullScreen
        open={payWallOpen}
        handleClose={() => setPayWallOpen(false)}
      >
        <PaywallPopup
          handleClose={() => setPayWallOpen(false)}
          type={PaywallTypes.TYPE_ONE_TIME}
          placement={ProductInvoicePlacements.sent_likes_list_banner_paywall}
          hideBackButton
          prevHeaderTemplate="light"
        />
      </Modal>
    </>
  );
};

export default SentLikesList;
